import { AlertDialog as BaseAlertDialog } from "@base-ui/react/alert-dialog";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import {
  "ui-overlay.cancel" as cancelText,
  "ui-overlay.close" as closeLabel,
} from "@repo/i18n";
import type { ReactNode } from "react";
import { cx } from "../../lib/cx";
import {
  CloseGlyph,
  DANGER_BUTTON,
  ICON_BUTTON,
  PRIMARY_BUTTON,
  QUIET_BUTTON,
  SCRIM,
} from "./parts";
import { useLayerPresence } from "./presence";

/** The centred card both dialogs share; left of the docked coach on desktop. */
const VIEWPORT =
  "fixed inset-0 z-50 flex items-center justify-center p-4 lg:right-[var(--coach-dock,0px)]";
const POPUP =
  "relative flex max-h-[88dvh] w-full max-w-[440px] flex-col overflow-hidden rounded-3xl bg-canvas text-ink shadow-lift outline-none transition-[opacity,transform] duration-200 data-ending-style:scale-95 data-starting-style:scale-95 data-ending-style:opacity-0 data-starting-style:opacity-0";

export interface DialogProps {
  children?: ReactNode;
  /** Merged onto the popup with `cx`. */
  className?: string;
  description?: ReactNode;
  footer?: ReactNode;
  /** `false` leaves the page behind interactive (no trap, no scroll lock). */
  modal?: boolean;
  /** Bind `false` to the router's `closeLayer()` (D02, T10). */
  onOpenChange: (open: boolean) => void;
  open: boolean;
  /** The heading, and the accessible name. */
  title: ReactNode;
}

/** A centred modal over Base UI Dialog. Controlled; knows nothing of the router. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  modal,
  className,
}: DialogProps) {
  const isModal = modal ?? true;
  // A dialog never owns a layer's exit, but it closes with the layer it sits
  // in (`D193`) rather than outliving it for the length of the slide.
  const { present } = useLayerPresence();
  return (
    <BaseDialog.Root
      modal={isModal}
      onOpenChange={onOpenChange}
      open={open && present}
    >
      <BaseDialog.Portal>
        {isModal ? <BaseDialog.Backdrop className={cx(SCRIM, "z-50")} /> : null}
        <BaseDialog.Viewport
          className={cx(VIEWPORT, !isModal && "pointer-events-none")}
        >
          <BaseDialog.Popup
            className={cx(POPUP, "pointer-events-auto", className)}
          >
            <div className="flex shrink-0 items-start gap-3 px-5 pt-5 lg:px-7 lg:pt-7">
              <div className="min-w-0 flex-1 pt-1">
                <BaseDialog.Title className="a-display text-balance text-2xl">
                  {title}
                </BaseDialog.Title>
                {description ? (
                  <BaseDialog.Description className="mt-2 text-base text-ink-2">
                    {description}
                  </BaseDialog.Description>
                ) : null}
              </div>
              <BaseDialog.Close
                aria-label={closeLabel()}
                className={ICON_BUTTON}
              >
                <CloseGlyph />
              </BaseDialog.Close>
            </div>
            {children ? (
              <div className="a-scroll min-h-0 flex-1 px-5 pt-4 pb-6 lg:px-7">
                {children}
              </div>
            ) : null}
            {footer ? (
              <div className="shrink-0 border-line border-t px-5 pt-3 pb-5 lg:px-7 lg:pb-6">
                {footer}
              </div>
            ) : null}
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

export interface AlertDialogProps {
  /** Defaults to "Cancelar". */
  cancelLabel?: ReactNode;
  /** Extra content between the description and the buttons. */
  children?: ReactNode;
  confirmLabel: ReactNode;
  description?: ReactNode;
  /**
   * A third action, drawn UNDER the two buttons as a full-width quiet
   * button — a real choice, not a link hidden in the content (`D220`'s
   * "Cancelar treino" was one, and was not found).
   */
  extraAction?: {
    readonly label: ReactNode;
    readonly onClick: () => void;
    readonly testId?: string;
  };
  onConfirm: () => void;
  /**
   * Called with `false` for Cancel and Escape. NOT called by the confirm
   * button: `onConfirm` decides what happens next (T55 ends the session and
   * replaces the layer with the summary), so a confirm never also pops history.
   */
  onOpenChange: (open: boolean) => void;
  open: boolean;
  /** Disables the confirm button, e.g. while the request is in flight. */
  pending?: boolean;
  title: ReactNode;
  /** `"destructive"` paints the confirm button in `danger` (T51 account deletion). */
  tone?: "default" | "destructive";
}

/**
 * A confirmation over Base UI AlertDialog: no outside-press dismissal, focus
 * starts inside, Escape and Cancel close. Used by T55's end-confirm and T51's
 * account deletion.
 */
export function AlertDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  cancelLabel,
  tone,
  pending,
  children,
  extraAction,
}: AlertDialogProps) {
  const { present } = useLayerPresence();
  return (
    <BaseAlertDialog.Root onOpenChange={onOpenChange} open={open && present}>
      <BaseAlertDialog.Portal>
        <BaseAlertDialog.Backdrop className={cx(SCRIM, "z-50")} />
        <BaseAlertDialog.Viewport className={VIEWPORT}>
          <BaseAlertDialog.Popup className={cx(POPUP, "px-5 pt-6 pb-5")}>
            <BaseAlertDialog.Title className="a-display text-balance text-2xl">
              {title}
            </BaseAlertDialog.Title>
            {description ? (
              <BaseAlertDialog.Description className="mt-2 text-base text-ink-2">
                {description}
              </BaseAlertDialog.Description>
            ) : null}
            {children ? <div className="mt-4">{children}</div> : null}
            {/*
              `flex-col-reverse`: the confirm button is second in the DOM, so
              focus order and Base UI's `Close` semantics stay as they were,
              while the confirm paints on top. Side by side inside a 440 px
              popup, a pt-BR pair like "Continuar treinando" / "Encerrar
              treino" left the primary about 96 px and wrapped it onto two
              lines (T72). `flex-none` and `w-full` undo `parts.tsx`'s
              row-shaped `flex-1` / `shrink-0`, which in a column would size
              the confirm by its text rather than its `h-14`.
            */}
            <div className="mt-6 flex flex-col-reverse gap-2">
              {/* First in the DOM, so the column paints it last: under the
                  confirm and the cancel. */}
              {extraAction === undefined ? null : (
                <button
                  className={cx(QUIET_BUTTON, "w-full text-danger")}
                  data-testid={extraAction.testId}
                  disabled={pending}
                  onClick={extraAction.onClick}
                  type="button"
                >
                  {extraAction.label}
                </button>
              )}
              <BaseAlertDialog.Close className={cx(QUIET_BUTTON, "w-full")}>
                {cancelLabel ?? cancelText()}
              </BaseAlertDialog.Close>
              <button
                className={cx(
                  tone === "destructive" ? DANGER_BUTTON : PRIMARY_BUTTON,
                  "w-full flex-none"
                )}
                data-tone={tone ?? "default"}
                disabled={pending}
                onClick={onConfirm}
                type="button"
              >
                {confirmLabel}
              </button>
            </div>
          </BaseAlertDialog.Popup>
        </BaseAlertDialog.Viewport>
      </BaseAlertDialog.Portal>
    </BaseAlertDialog.Root>
  );
}
